import API from "../services/api";

const draftBuckets = new Map();
const deletedDraftBuckets = new Map();
const STORAGE_PREFIX = "blackroth_draft_mails:";
const DELETED_STORAGE_PREFIX = "blackroth_deleted_draft_mails:";
const clearedLegacyStorageScopes = new Set();

const clone = (value) => {
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value));
  }
};

const getBucketKey = (scope = "bulk-emails") => String(scope || "bulk-emails");

const getStorageKey = (scope) => `${STORAGE_PREFIX}${getBucketKey(scope)}`;
const getDeletedStorageKey = (scope) => `${DELETED_STORAGE_PREFIX}${getBucketKey(scope)}`;

const clearLegacyStoredBuckets = (scope) => {
  const key = getBucketKey(scope);

  if (clearedLegacyStorageScopes.has(key)) {
    return;
  }

  clearedLegacyStorageScopes.add(key);

  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }

    window.localStorage.removeItem(getStorageKey(scope));
    window.localStorage.removeItem(getDeletedStorageKey(scope));

    Object.keys(window.localStorage)
      .filter(
        (storageKey) =>
          storageKey.startsWith(STORAGE_PREFIX) ||
          storageKey.startsWith(DELETED_STORAGE_PREFIX),
      )
      .forEach((storageKey) => window.localStorage.removeItem(storageKey));
  } catch {
    // Legacy cleanup is best-effort; bulk mail drafts now stay in memory only.
  }
};

const getBucket = (scope) => {
  const key = getBucketKey(scope);

  if (!draftBuckets.has(key)) {
    clearLegacyStoredBuckets(scope);
    draftBuckets.set(key, []);
  }

  return draftBuckets.get(key) || [];
};

const setBucket = (scope, items) => {
  const nextItems = Array.isArray(items) ? items : [];
  draftBuckets.set(getBucketKey(scope), nextItems);
};

const getDeletedBucket = (scope) => {
  const key = getBucketKey(scope);

  if (!deletedDraftBuckets.has(key)) {
    clearLegacyStoredBuckets(scope);
    deletedDraftBuckets.set(key, []);
  }

  return deletedDraftBuckets.get(key) || [];
};

const setDeletedBucket = (scope, items) => {
  const nextItems = Array.isArray(items) ? items : [];
  deletedDraftBuckets.set(getBucketKey(scope), nextItems);
};

export const listDraftMails = (scope = "bulk-emails") =>
  getBucket(scope).map((item) => clone(item));

export const upsertDraftMail = (draft, scope = "bulk-emails") => {
  const nextDraft = clone(draft);
  const current = getBucket(scope);
  const next = current.filter((item) => item.id !== nextDraft.id);
  next.unshift(nextDraft);
  setBucket(scope, next);
  return clone(nextDraft);
};

export const removeDraftMail = (id, scope = "bulk-emails") => {
  const key = String(id || "").trim();
  if (!key) {
    return [];
  }

  const next = getBucket(scope).filter((item) => item.id !== key);
  setBucket(scope, next);
  return next.map((item) => clone(item));
};

export const clearDraftMails = (scope = "bulk-emails") => {
  setBucket(scope, []);
};

export const listDeletedDraftMails = (scope = "bulk-emails") =>
  getDeletedBucket(scope).map((item) => clone(item));

export const upsertDeletedDraftMail = (draft, scope = "bulk-emails") => {
  const nextDraft = clone(draft);
  const current = getDeletedBucket(scope);
  const next = current.filter((item) => item.id !== nextDraft.id);
  next.unshift(nextDraft);
  setDeletedBucket(scope, next);
  return clone(nextDraft);
};

export const removeDeletedDraftMail = (id, scope = "bulk-emails") => {
  const key = String(id || "").trim();
  if (!key) {
    return [];
  }

  const next = getDeletedBucket(scope).filter((item) => item.id !== key);
  setDeletedBucket(scope, next);
  return next.map((item) => clone(item));
};

export const fetchMailArchive = async (params = {}) => {
  const response = await API.get("/admin/mail-archive", { params });
  return Array.isArray(response?.data?.data) ? response.data.data : [];
};

export const moveMailArchiveFolder = async (ids, folder) => {
  return API.patch("/admin/mail-archive/folder", {
    ids,
    folder,
  });
};

export const deleteMailArchiveItems = async (ids) => {
  return API.delete("/admin/mail-archive", {
    data: {
      ids,
    },
  });
};
